import React from "react";
import { Input, Space, Typography, Alert, Tabs, ColorPicker, Checkbox } from "antd";
import MediaPicker from "../../../components/mediaPicker";
import IconPicker from "../../../components/cms-common/IconPicker";
import {
  newId,
  listOps,
  FieldGrid,
  WrapRow,
  Field,
  PanelCard,
  ItemBox,
  RowActions,
  AddItemButton,
  LinkField,
  LinkListEditor,
} from "./layoutFormKit";

const { Text } = Typography;
const { TextArea } = Input;

// Column layout follows tykkit's footer: 1 = brand/contact/social/payments,
// 2 & 3 = titled link lists, 4 = happy-customers counter + badges.
// Copyright and colours sit outside the columns.

// Image + name (+ optional link) rows — used for payment methods and trust badges.
const ImageListEditor = ({ items = [], onChange, addLabel, namePlaceholder, withLink }) => {
  const ops = listOps(items, onChange);
  return (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      {items.map((item, index) => (
        <ItemBox key={item.id || index}>
          <WrapRow align="center" gap={12}>
            <div style={{ flex: "none" }}>
              <MediaPicker
                max={1}
                value={item.image || []}
                onChange={(image) => ops.update(index, { image })}
              />
            </div>
            <Space direction="vertical" size={8} style={{ flex: "1 1 240px", minWidth: 0 }}>
              <Input
                value={item.name || ""}
                onChange={(e) => ops.update(index, { name: e.target.value })}
                placeholder={namePlaceholder}
              />
              {withLink && (
                <LinkField
                  value={item.link || { type: "external", url: "" }}
                  onChange={(link) => ops.update(index, { link })}
                />
              )}
            </Space>
            <RowActions
              index={index}
              total={items.length}
              onMove={ops.move}
              onRemove={ops.remove}
            />
          </WrapRow>
        </ItemBox>
      ))}
      <AddItemButton
        label={addLabel}
        onClick={() =>
          ops.add({
            id: newId("img"),
            name: "",
            image: [],
            ...(withLink ? { link: { type: "external", url: "" } } : {}),
          })
        }
      />
    </Space>
  );
};

// Hex colour field; empty = the site's default colour.
const ColorField = ({ label, hint, value, onChange, allowTransparent }) => {
  const isTransparent = value === "transparent";
  return (
    <Field label={label}>
      <WrapRow align="center">
        <ColorPicker
          // antd labels an empty picker "Transparent"; here empty means the site default.
          showText={(color) =>
            isTransparent ? "Transparent" : value ? color.toHexString() : "Default"
          }
          allowClear
          disabled={isTransparent}
          value={isTransparent ? undefined : value || undefined}
          onChange={(color) => {
            // An empty picker starts at 0% alpha; the first pick should be a solid colour.
            const hex = color.toHexString();
            onChange(!value && color.toRgb().a === 0 ? hex.slice(0, 7) : hex);
          }}
          onClear={() => onChange("")}
        />
        {allowTransparent && (
          <Checkbox
            checked={isTransparent}
            onChange={(e) => onChange(e.target.checked ? "transparent" : "")}
          >
            Transparent
          </Checkbox>
        )}
      </WrapRow>
      {hint && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Text>
      )}
    </Field>
  );
};

const Footer = ({ data = {}, onChange }) => {
  const set = (field, value) => onChange({ ...data, [field]: value });
  const setIn = (group, field, value) =>
    onChange({ ...data, [group]: { ...(data[group] || {}), [field]: value } });

  // Footers saved before the column layout kept links in `link_columns`.
  const legacyColumns = data.link_columns || [];
  const getColumn = (n) => data[`column_${n}`] || legacyColumns[n - 2] || { title: "", links: [] };
  const setColumn = (n, patch) => {
    const { link_columns, ...rest } = data;
    onChange({
      ...rest,
      column_2: getColumn(2),
      column_3: getColumn(3),
      [`column_${n}`]: { ...getColumn(n), ...patch },
    });
  };

  const contact = data.contact || {};
  const stats = data.stats || {};
  const payments = data.payments || {};
  const style = data.style || {};
  const socialLinks = data.social_links || [];
  const social = listOps(socialLinks, (next) => set("social_links", next));

  const linkColumnTab = (n) => {
    const column = getColumn(n);
    return (
      <PanelCard
        icon="links-line"
        color="#fa8c16"
        title={`Column ${n}: Links (${(column.links || []).length})`}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Field label="Column Title">
            <Input
              value={column.title || ""}
              onChange={(e) => setColumn(n, { title: e.target.value })}
              placeholder={n === 2 ? "e.g. About Infotainment.com" : "e.g. Important Links"}
            />
          </Field>
          <LinkListEditor
            links={column.links || []}
            onChange={(links) => setColumn(n, { links })}
          />
        </Space>
      </PanelCard>
    );
  };

  const tabs = [
    {
      key: "col1",
      label: "Column 1",
      children: (
        <>
          <PanelCard icon="building-line" color="#1890ff" title="Logo & Contact Details">
            <WrapRow align="flex-start" gap={16}>
              <Field label="Footer Logo (light version)" flex="0 0 auto">
                <MediaPicker max={1} value={data.logo || []} onChange={(val) => set("logo", val)} />
              </Field>
              <Space direction="vertical" size={16} style={{ flex: "1 1 300px", minWidth: 0 }}>
                <Field label="Logo Alt Text">
                  <Input
                    value={data.logo_alt || ""}
                    onChange={(e) => set("logo_alt", e.target.value)}
                    placeholder="e.g. Infotainment Repair"
                  />
                </Field>
                <WrapRow>
                  <Field label="Address Label" flex="0 1 120px">
                    <Input
                      value={contact.address_label || ""}
                      onChange={(e) => setIn("contact", "address_label", e.target.value)}
                      placeholder="HQ:"
                    />
                  </Field>
                  <Field label="Address" flex="1 1 240px">
                    <Input
                      value={contact.address || ""}
                      onChange={(e) => setIn("contact", "address", e.target.value)}
                      placeholder="500 geneva Drive Oviedo, FL 32765"
                    />
                  </Field>
                </WrapRow>
                <WrapRow>
                  <Field label="Phone Label" flex="0 1 120px">
                    <Input
                      value={contact.phone_label || ""}
                      onChange={(e) => setIn("contact", "phone_label", e.target.value)}
                      placeholder="PH:"
                    />
                  </Field>
                  <Field label="Phone Number" flex="1 1 240px">
                    <Input
                      value={contact.phone || ""}
                      onChange={(e) => setIn("contact", "phone", e.target.value)}
                      placeholder="855-636-6387"
                    />
                  </Field>
                </WrapRow>
              </Space>
            </WrapRow>
          </PanelCard>

          <PanelCard
            icon="share-line"
            color="#eb2f96"
            title={`Social Icons (${socialLinks.length})`}
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              {socialLinks.map((item, index) => (
                <ItemBox key={item.id || index}>
                  <WrapRow>
                    <Field label={index === 0 ? "Icon" : null} flex="none">
                      <IconPicker
                        value={item.icon || undefined}
                        onChange={(icon) => social.update(index, { icon })}
                      />
                    </Field>
                    <Field label={index === 0 ? "Label" : null} flex="1 1 150px">
                      <Input
                        value={item.label || ""}
                        onChange={(e) => social.update(index, { label: e.target.value })}
                        placeholder="e.g. Instagram"
                      />
                    </Field>
                    <Field label={index === 0 ? "Link" : null} flex="2 1 300px">
                      {/* Saved as { type, url } like every other link; older rows kept a plain `url` string. */}
                      <LinkField
                        value={item.link || (item.url ? item.url : { type: "external", url: "" })}
                        onChange={(link) => social.update(index, { link, url: undefined })}
                      />
                    </Field>
                    <RowActions
                      index={index}
                      total={socialLinks.length}
                      onMove={social.move}
                      onRemove={social.remove}
                    />
                  </WrapRow>
                </ItemBox>
              ))}
              <AddItemButton
                label="Add Social Icon"
                onClick={() =>
                  social.add({
                    id: newId("soc"),
                    icon: "",
                    label: "",
                    link: { type: "external", url: "" },
                  })
                }
              />
            </Space>
          </PanelCard>

          <PanelCard icon="bank-card-line" color="#52c41a" title="Accepted Payments">
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Field label="Title">
                <Input
                  value={payments.title || ""}
                  onChange={(e) => setIn("payments", "title", e.target.value)}
                  placeholder="Accepted Payments"
                />
              </Field>
              <ImageListEditor
                items={payments.items || []}
                onChange={(items) => setIn("payments", "items", items)}
                addLabel="Add Payment Method"
                namePlaceholder="e.g. Visa"
              />
            </Space>
          </PanelCard>
        </>
      ),
    },
    { key: "col2", label: "Column 2", children: linkColumnTab(2) },
    { key: "col3", label: "Column 3", children: linkColumnTab(3) },
    {
      key: "col4",
      label: "Column 4",
      children: (
        <PanelCard icon="award-line" color="#8b0000" title="Happy Customers & Badges">
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <FieldGrid min={150}>
              <Field label="Counter Value">
                <Input
                  value={stats.count || ""}
                  onChange={(e) => setIn("stats", "count", e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="90"
                />
              </Field>
              <Field label="Counter Suffix">
                <Input
                  value={stats.suffix || ""}
                  onChange={(e) => setIn("stats", "suffix", e.target.value)}
                  placeholder="K+"
                />
              </Field>
            </FieldGrid>
            <Field label="Counter Title">
              <Input
                value={stats.title || ""}
                onChange={(e) => setIn("stats", "title", e.target.value)}
                placeholder="Happy Customers & Counting"
              />
            </Field>
            <Field label="Description">
              <TextArea
                rows={2}
                value={stats.description || ""}
                onChange={(e) => setIn("stats", "description", e.target.value)}
                placeholder="Customer Satisfaction is the key to our success."
              />
            </Field>
            <Field label="Badges (eBay, BBB, …)">
              <ImageListEditor
                items={stats.badges || []}
                onChange={(badges) => setIn("stats", "badges", badges)}
                addLabel="Add Badge"
                namePlaceholder="e.g. BBB Accredited"
                withLink
              />
            </Field>
          </Space>
        </PanelCard>
      ),
    },
    {
      key: "copyright",
      label: "Copyright",
      children: (
        <PanelCard icon="copyright-line" color="#595959" title="Copyright Bar">
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Field label="Copyright Text">
              <Input
                value={data.copyright || ""}
                onChange={(e) => set("copyright", e.target.value)}
                placeholder="© {{year}} Infotainment.com - Discover Your Vehicle's Potential. All Rights Reserved"
              />
            </Field>
            <Alert
              type="info"
              showIcon
              style={{ fontSize: 12 }}
              message={
                <span>
                  Use <code>{"{{year}}"}</code> to insert the current year automatically.
                </span>
              }
            />
          </Space>
        </PanelCard>
      ),
    },
    {
      key: "style",
      label: "Colors",
      children: (
        <PanelCard icon="palette-line" color="#722ed1" title="Footer Colors">
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <FieldGrid min={220}>
              <ColorField
                label="Background Color"
                allowTransparent
                value={style.background_color}
                onChange={(val) => setIn("style", "background_color", val)}
              />
              <ColorField
                label="Text Color"
                hint="Applies to all footer text."
                value={style.text_color}
                onChange={(val) => setIn("style", "text_color", val)}
              />
              <ColorField
                label="Heading Color"
                hint="Column titles and counter. Empty = text color."
                value={style.heading_color}
                onChange={(val) => setIn("style", "heading_color", val)}
              />
              <ColorField
                label="Link Color"
                hint="Footer links. Empty = text color."
                value={style.link_color}
                onChange={(val) => setIn("style", "link_color", val)}
              />
            </FieldGrid>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Leave a color empty to keep the site's default.
            </Text>
          </Space>
        </PanelCard>
      ),
    },
  ];

  return (
    <div style={{ padding: "8px 0" }}>
      <Tabs type="card" items={tabs.map((t) => ({ ...t, forceRender: true }))} />
    </div>
  );
};

export default Footer;
